import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Avatar,
  Icon,
  useColorModeValue,
  Tooltip,
} from "@chakra-ui/react";
import {
  MailOutlined,
  PhoneOutlined,
  CopyOutlined,
  RightOutlined,
  EditOutlined,
  EnvironmentFilled,
  EnvironmentOutlined,
  BankOutlined,
  BlockOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import placeHolderImage from "assets/img/avatars/avatar-placeholder.png";
import Card from "components/card/Card.js";
import moment from "moment";
import coverImage from "assets/img/Serj_Covers-Collaborations-and-Collages_1-cropped_Credit-Travis-Shinn.jpeg";

const ProfileCard = ({ data, onEditClick, onAvatarChange }) => {
  const textColor = useColorModeValue("#2D3748", "white");
  const secondaryColor = useColorModeValue("#718096", "secondaryGray.500");
  const cardBg = useColorModeValue("white", "navy.800");
  const iconColor = "#4A5568";
  const brandColor = "#4299E1";
  const fileInputRef = useRef(null);
  const localAvatarUrlRef = useRef("");
  const [localAvatarSrc, setLocalAvatarSrc] = useState("");

  const handleCopy = (text) => {
    if (text) {
      navigator.clipboard.writeText(text);
      message.success("Copied to clipboard!");
    }
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      message.error("Please select an image file.");
      event.target.value = "";
      return;
    }

    const nextAvatarUrl = URL.createObjectURL(file);

    if (localAvatarUrlRef.current) {
      URL.revokeObjectURL(localAvatarUrlRef.current);
    }

    localAvatarUrlRef.current = nextAvatarUrl;
    setLocalAvatarSrc(nextAvatarUrl);
    event.target.value = "";

    if (onAvatarChange) {
      await onAvatarChange(file);
    }
  };

  useEffect(() => {
    return () => {
      if (localAvatarUrlRef.current) {
        URL.revokeObjectURL(localAvatarUrlRef.current);
      }
    };
  }, []);

  const fullName =
    data?.business_name ||
    data?.fullname ||
    `${data?.first_name || ""} ${data?.last_name || ""}`.trim();
  const memberSince = data?.created_at
    ? moment(data.created_at).format("MMM YYYY")
    : "Jan 2024";
  const avatarSrc = localAvatarSrc || data?.profile_picture || placeHolderImage;

  return (
    <Card
      pb={{ base: "24px", md: "32px" }}
      px={{ base: "16px", md: "24px", lg: "32px" }}
      pt={{ base: "100px", sm: "110px", md: "125px" }}
      borderRadius={{ base: "24px", md: "32px", lg: "40px" }}
      border={{
        base: "4px solid white",
        md: "6px solid white",
        lg: "10px solid white",
      }}
      width="100%"
      bg={cardBg}
    >
      <Flex direction="column" w="100%">
        <Flex
          justify={{ base: "center", md: "space-between" }}
          align={{ base: "center", md: "center" }}
          direction={{ base: "column", md: "row" }}
          mb={{ base: "20px", md: "30px" }}
          position="absolute"
          top={{ base: "10px", md: "5px" }}
          left={{ base: "50%", md: "30px", lg: "50px" }}
          right={{ base: "auto", md: "30px", lg: "50px" }}
          transform={{ base: "translateX(-50%)", md: "none" }}
          gap={{ base: "12px", md: "0" }}
          width={{
            base: "auto",
            md: "calc(100% - 60px)",
            lg: "calc(100% - 100px)",
          }}
          zIndex="1"
        >
          <Flex
            align="center"
            direction={{ base: "column", sm: "row" }}
            textAlign={{ base: "center", sm: "left" }}
          >
            <Box position="relative">
              <Avatar
                h={{ base: "90px", sm: "100px", md: "110px", lg: "140px" }}
                w={{ base: "90px", sm: "100px", md: "110px", lg: "140px" }}
                src={avatarSrc}
                p="4px"
                boxShadow="inset 0 0 0 1px rgba(0,0,0,0.05), 0px 10px 20px rgba(0,0,0,0.05)"
                mr={{ base: "0", sm: "16px", md: "20px", lg: "24px" }}
                mt={{ base: "10px", md: "15px", lg: "20px" }}
                mb={{ base: "8px", sm: "0" }}
                border={{ base: "3px solid white", md: "4px solid white" }}
                onClick={handleAvatarClick}
                cursor="pointer"
                title="Click to change profile picture"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: "none" }}
              />
            </Box>
            <Box>
              <Text
                color={textColor}
                fontSize={{ base: "18px", sm: "20px", md: "22px", lg: "24px" }}
                fontWeight="700"
                letterSpacing="-0.5px"
              >
                {fullName || "Serj Tankian"}
              </Text>
              <Text
                color={secondaryColor}
                fontSize={{ base: "13px", md: "14px", lg: "16px" }}
                fontWeight="400"
                mt={{ base: "2px", md: "4px" }}
              >
                Member since: {memberSince}
              </Text>
            </Box>
          </Flex>
        </Flex>

        <Box
          bg="white"
          borderRadius={{ base: "16px", md: "20px", lg: "24px" }}
          pb={{ base: "20px", md: "25px", lg: "30px" }}
          px={{ base: "16px", md: "24px", lg: "30px" }}
          pt={{ base: "50px", md: "60px", lg: "70px" }}
          border="1px solid"
          borderColor="#F1F4F9"
          boxShadow="
            0px 8px 24px rgba(220, 228, 242, 0.85),
            0px 24px 64px rgba(232, 236, 246, 0.75),
            inset 0px 1px 0px rgba(255, 255, 255, 0.65)
          "
          position="relative"
        >
          <Box
            as="button"
            onClick={onEditClick}
            position="absolute"
            top={{ base: "12px", md: "15px" }}
            right={{ base: "15px", md: "25px" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="#F7FAFC"
            color="#3182CE"
            px={{ base: "10px", md: "12px" }}
            py={{ base: "4px", md: "6px" }}
            borderRadius="8px"
            fontWeight="600"
            fontSize={{ base: "12px", md: "13px" }}
            transition="all 0.2s ease"
            _hover={{ bg: "#E2E8F0" }}
            border="1px solid"
            borderColor="#E2E8F0"
            zIndex="2"
          >
            <Icon as={EditOutlined} mr="6px" />
            Edit
            <Icon as={RightOutlined} ml="8px" fontSize="10px" opacity="0.7" />
          </Box>
          <Box
            h="1.5px"
            bg="#EDF2F7"
            mb={{ base: "20px", md: "25px" }}
            display={{ base: "none", md: "block" }}
          />
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            gap={{ base: "24px", md: "0" }}
          >
            <Flex direction="column" flex="1">
              <Box mb={{ base: "20px", md: "28px" }}>
                <Flex align="center" mb={{ base: "8px", md: "10px" }}>
                  <Box
                    bg="#EDF2F7"
                    p={{ base: "5px", md: "6px" }}
                    borderRadius="6px"
                    display="flex"
                    mr={{ base: "8px", md: "10px" }}
                  >
                    <Icon
                      as={MailOutlined}
                      color={brandColor}
                      fontSize={{ base: "14px", md: "16px" }}
                    />
                  </Box>
                  <Text
                    color={secondaryColor}
                    fontSize={{ base: "13px", md: "14px" }}
                    fontWeight="600"
                  >
                    Email
                  </Text>
                </Flex>
                <Flex
                  align="center"
                  flexWrap="wrap"
                  gap={{ base: "8px", md: "0" }}
                >
                  <Text
                    color={textColor}
                    fontSize={{ base: "14px", md: "15px", lg: "16px" }}
                    fontWeight="500"
                    mr={{ base: "0", md: "12px" }}
                    wordBreak="break-all"
                  >
                    {data?.email || "aliresa.1998@gmail.com"}
                  </Text>
                  <Tooltip label="Copy" hasArrow>
                    <Box
                      as="button"
                      onClick={() =>
                        handleCopy(data?.email || "aliresa.1998@gmail.com")
                      }
                      p={{ base: "5px", md: "6px" }}
                      borderRadius="8px"
                      bg="#EDF2F7"
                      color={brandColor}
                      display="flex"
                      transition="0.2s"
                      _hover={{ bg: "#E2E8F0" }}
                      flexShrink="0"
                    >
                      <Icon
                        as={CopyOutlined}
                        fontSize={{ base: "12px", md: "14px" }}
                      />
                    </Box>
                  </Tooltip>
                </Flex>
              </Box>

              {/* <Box
                h="1.5px"
                bg="#EDF2F7"
                mx="15px"
                mb="25px"
                display={{ base: "none", md: "block" }}
              /> */}

              <Box>
                <Flex align="center" mb={{ base: "8px", md: "10px" }}>
                  <Box
                    bg="#EDF2F7"
                    p={{ base: "5px", md: "6px" }}
                    borderRadius="6px"
                    display="flex"
                    mr={{ base: "8px", md: "10px" }}
                  >
                    <Icon
                      as={PhoneOutlined}
                      color={brandColor}
                      fontSize={{ base: "14px", md: "16px" }}
                    />
                  </Box>
                  <Text
                    color={secondaryColor}
                    fontSize={{ base: "13px", md: "14px" }}
                    fontWeight="600"
                  >
                    Phone
                  </Text>
                </Flex>
                <Text
                  color={textColor}
                  fontSize={{ base: "14px", md: "15px", lg: "16px" }}
                  fontWeight="500"
                >
                  {data?.phone || "1234567890"}
                </Text>
              </Box>
            </Flex>

            {/* Horizontal divider for mobile, Vertical divider for tablet+ */}
            <Box
              display={{ base: "block", md: "none" }}
              h="1px"
              bg="#EDF2F7"
              w="100%"
              my="4px"
            />
            <Box
              w="1.5px"
              bg="#EDF2F7"
              mx={{ base: "0", md: "20px", lg: "40px" }}
              display={{ base: "none", md: "block" }}
            />

            <Flex
              direction="column"
              flex="1"
              gap={{ base: "14px", md: "16px", lg: "18px" }}
              mt={{ base: "0", md: "0" }}
            >
              <Flex align="center">
                <Icon
                  as={EnvironmentFilled}
                  color={brandColor}
                  mr={{ base: "10px", md: "12px" }}
                  fontSize={{ base: "16px", md: "18px" }}
                />
                <Text
                  color={textColor}
                  fontSize={{ base: "14px", md: "15px", lg: "16px" }}
                  fontWeight="500"
                >
                  {data?.state || "TN"}
                </Text>
              </Flex>
              <Flex align="center">
                <Icon
                  as={BankOutlined}
                  color="#A0AEC0"
                  mr={{ base: "10px", md: "12px" }}
                  fontSize={{ base: "16px", md: "18px" }}
                />
                <Text
                  color={textColor}
                  fontSize={{ base: "14px", md: "15px", lg: "16px" }}
                  fontWeight="500"
                >
                  {data?.city || "Toronto"}
                </Text>
              </Flex>
              <Flex align="center">
                <Icon
                  as={EnvironmentOutlined}
                  color="#ED8936"
                  mr={{ base: "10px", md: "12px" }}
                  fontSize={{ base: "16px", md: "18px" }}
                />
                <Text
                  color={textColor}
                  fontSize={{ base: "14px", md: "15px", lg: "16px" }}
                  fontWeight="500"
                >
                  {data?.address || "648 Yonge yang"}
                </Text>
              </Flex>
              <Flex align="center">
                <Icon
                  as={BlockOutlined}
                  color="#A0AEC0"
                  mr={{ base: "10px", md: "12px" }}
                  fontSize={{ base: "16px", md: "18px" }}
                />
                <Text
                  color={textColor}
                  fontSize={{ base: "14px", md: "15px", lg: "16px" }}
                  fontWeight="500"
                >
                  {data?.zip_code || "A1A1A1"}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Card>
  );
};

export default ProfileCard;
